import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import QRCode from 'react-qr-code'
import { Copy } from 'lucide-react'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Label } from '@/common/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Input } from '@/common/ui/input'
import Spinner from '@/common/components/Spinner'
import showErrorToast from '@/common/utils/showErrorToast'

import useMutationGeneratePayment from '@/modules/crypto-invoices/api/hooks/useMutationGeneratePayment'
import useMutationConfirmTransaction from '@/modules/crypto-invoices/api/hooks/useMutationConfirmTransaction'
import { CryptoInvoiceQueryKeys } from '@/modules/crypto-invoices/keys'

import type {
	CryptoInvoiceData,
	ChainType,
	GeneratePaymentResponse,
} from '@/modules/crypto-invoices/type'

interface GeneratePaymentModalProps {
	invoice: CryptoInvoiceData
	isOpen: boolean
	onClose: () => void
}

const chainOptions: { value: ChainType; label: string }[] = [
	{ value: 'TRC20', label: 'TRC20 (USDT)' },
	{ value: 'POLYGON', label: 'Polygon' },
]

export default function GeneratePaymentModal({
	invoice,
	isOpen,
	onClose,
}: GeneratePaymentModalProps) {
	const [selectedChain, setSelectedChain] = useState<ChainType>('POLYGON')
	const [paymentData, setPaymentData] =
		useState<GeneratePaymentResponse | null>(null)
	const [showConfirmStep, setShowConfirmStep] = useState(false)
	const [txHash, setTxHash] = useState('')

	const queryClient = useQueryClient()
	const { mutateAsync: generatePayment, isPending } =
		useMutationGeneratePayment()
	const { mutateAsync: confirmTransaction, isPending: isConfirming } =
		useMutationConfirmTransaction()

	// Load existing Wallet Address when modal opens
	useEffect(() => {
		if (isOpen && invoice.walletAddress) {
			setPaymentData(invoice)
		}
		// Reset confirm step if status is completed
		if (isOpen && invoice.status === 'COMPLETED') {
			setShowConfirmStep(false)
		}
	}, [isOpen, invoice])

	const handleGenerate = async () => {
		try {
			const result = await generatePayment({
				id: invoice.id,
				chain: selectedChain,
			})

			setPaymentData(result)
			toast.success('สร้าง Wallet Address สำเร็จ')

			// Invalidate queries to refresh the table
			queryClient.invalidateQueries({
				queryKey: [CryptoInvoiceQueryKeys.CryptoInvoices],
			})
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleClose = () => {
		if (!invoice.walletAddress) {
			setPaymentData(null)
		}
		setShowConfirmStep(false)
		setTxHash('')
		onClose()
	}

	const handleConfirm = async () => {
		if (!txHash.trim()) {
			toast.error('กรุณากรอก Transaction Hash')
			return
		}

		try {
			await confirmTransaction({
				id: invoice.id,
				txHash: txHash.trim(),
			})

			toast.success('ยืนยันธุรกรรมสำเร็จ')

			// Invalidate queries to refresh the table
			queryClient.invalidateQueries({
				queryKey: [CryptoInvoiceQueryKeys.CryptoInvoices],
			})

			// Close modal
			handleClose()
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>
						{!paymentData
							? 'สร้าง Wallet Address สำหรับชำระเงิน'
							: showConfirmStep
								? 'ยืนยันการชำระเงิน'
								: 'ข้อมูลการชำระเงิน'}
					</DialogTitle>
					<DialogDescription>
						{!paymentData
							? 'เลือกเครือข่ายและสร้าง Wallet Address สำหรับการชำระเงิน'
							: showConfirmStep
								? 'กรอก Transaction Hash เพื่อยืนยันการชำระเงิน'
								: 'สแกน QR Code หรือคัดลอก Wallet Address เพื่อโอนเงิน'}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{!paymentData ? (
						<>
							<div className="space-y-2">
								<Label>Transaction ID</Label>
								<div className="rounded-md border bg-muted p-2 font-mono text-sm">
									{invoice.txId}
								</div>
							</div>

							<div className="space-y-2">
								<Label>จำนวนเงิน</Label>
								<div className="rounded-md border bg-muted p-2 text-lg font-semibold">
									$
									{invoice.amount.toLocaleString('en-US', {
										minimumFractionDigits: 2,
									})}
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="chain">เลือกเครือข่าย</Label>
								<Select
									value={selectedChain}
									onValueChange={(value) =>
										setSelectedChain(value as ChainType)
									}
								>
									<SelectTrigger id="chain">
										<SelectValue placeholder="เลือกเครือข่าย" />
									</SelectTrigger>
									<SelectContent>
										{chainOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="flex justify-end gap-2 pt-4">
								<Button variant="outline" onClick={handleClose}>
									ยกเลิก
								</Button>
								<Button onClick={handleGenerate} disabled={isPending}>
									{isPending && <Spinner />}
									สร้าง Wallet Address
								</Button>
							</div>
						</>
					) : !showConfirmStep ? (
						<>
							<div className="space-y-4">
								{/* Show completion status if completed */}
								{invoice.status === 'COMPLETED' ? (
									<div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
										<div className="mb-2 flex items-center gap-2">
											<div className="h-2 w-2 rounded-full bg-green-500"></div>
											<span className="text-sm font-medium text-green-800">
												การชำระเงินเสร็จสมบูรณ์
											</span>
										</div>
										<p className="text-xs text-green-700">
											ธุรกรรมนี้ได้รับการยืนยันแล้ว
										</p>
										{invoice.txHash && (
											<div className="mt-2 break-all rounded-md bg-green-100 p-2 font-mono text-xs text-green-800">
												TX: {invoice.txHash}
											</div>
										)}
									</div>
								) : (
									<div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
										<div className="mb-2 flex items-center gap-2">
											<div className="h-2 w-2 rounded-full bg-green-500"></div>
											<span className="text-sm font-medium text-green-800">
												{invoice.walletAddress
													? 'ข้อมูลการชำระเงิน'
													: 'สร้างที่อยู่กระเป้าเงินสำเร็จ'}
											</span>
										</div>
										<p className="text-xs text-green-700">
											สแกน QR Code หรือคัดลอกที่อยู่กระเป้าเงินเพื่อโอนเงิน
										</p>
									</div>
								)}

								<div className="flex flex-col items-center space-y-3">
									<div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-4">
										{paymentData.walletAddress && (
											<QRCode
												value={paymentData.walletAddress}
												size={256}
												level="H"
												style={{
													height: 'auto',
													maxWidth: '100%',
													width: '100%',
												}}
											/>
										)}
									</div>
									<p className="text-center text-xs text-muted-foreground">
										สแกน QR Code เพื่อรับ Wallet Address
									</p>
								</div>

								<div className="space-y-2">
									<Label>Transaction ID</Label>
									<div className="rounded-md border bg-muted p-2 font-mono text-sm">
										{paymentData.txId}
									</div>
								</div>

								<div className="space-y-2">
									<Label>
										Wallet Address <span className="text-destructive">*</span>
									</Label>
									<div className="flex items-center gap-2">
										<div className="flex-1 break-all rounded-md border-2 border-primary bg-primary/5 p-3 font-mono text-sm font-semibold">
											{paymentData.walletAddress}
										</div>
										<Button
											variant="outline"
											size="icon"
											className="shrink-0"
											onClick={() => {
												navigator.clipboard.writeText(
													paymentData.walletAddress || '',
												)
												toast.success('คัดลอก Wallet Address แล้ว')
											}}
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
								</div>

								<div className="space-y-2">
									<Label>จำนวนเงิน</Label>
									<div className="rounded-md border bg-muted p-2 text-center text-lg font-semibold">
										$
										{paymentData.amount.toLocaleString('en-US', {
											minimumFractionDigits: 2,
										})}
									</div>
								</div>

								<div className="space-y-2">
									<Label>เครือข่าย</Label>
									<div className="rounded-md border bg-blue-100 p-2 text-center font-medium text-blue-800">
										{paymentData.chain}
									</div>
								</div>
							</div>

							<div className="flex justify-between gap-2 pt-4">
								<Button variant="outline" onClick={handleClose}>
									ปิด
								</Button>
								{/* Only show confirm button if status is not COMPLETED */}
								{invoice.status !== 'COMPLETED' && (
									<Button
										onClick={() => setShowConfirmStep(true)}
										disabled={isConfirming}
									>
										{isConfirming ? (
											<>
												<Spinner />
												กำลังยืนยัน...
											</>
										) : (
											'ยืนยันการชำระเงิน →'
										)}
									</Button>
								)}
							</div>
						</>
					) : (
						<>
							<div className="space-y-4">
								<div className="space-y-2">
									<Label>Transaction ID</Label>
									<div className="rounded-md border bg-muted p-2 font-mono text-sm">
										{paymentData.txId}
									</div>
								</div>

								<div className="space-y-2">
									<Label>จำนวนเงิน</Label>
									<div className="rounded-md border bg-muted p-2 text-lg font-semibold">
										$
										{paymentData.amount.toLocaleString('en-US', {
											minimumFractionDigits: 2,
										})}
									</div>
								</div>

								{paymentData.walletAddress && (
									<div className="space-y-2">
										<Label>กระเป้าเงิน</Label>
										<div className="break-all rounded-md border bg-muted p-2 font-mono text-xs">
											{paymentData.walletAddress}
										</div>
									</div>
								)}

								<div className="space-y-2">
									<Label htmlFor="txHash">
										Transaction Hash <span className="text-destructive">*</span>
									</Label>
									<Input
										id="txHash"
										placeholder="0x..."
										value={txHash}
										onChange={(e) => setTxHash(e.target.value)}
										className="font-mono text-sm"
									/>
									<p className="text-xs text-muted-foreground">
										กรอก Transaction Hash ที่ได้รับจากการโอนเงิน
									</p>
								</div>
							</div>

							<div className="flex justify-between gap-2 pt-4">
								<Button
									variant="outline"
									onClick={() => setShowConfirmStep(false)}
									disabled={isConfirming}
								>
									← ย้อนกลับ
								</Button>
								<Button
									onClick={handleConfirm}
									disabled={isConfirming || !txHash.trim()}
								>
									{isConfirming ? (
										<>
											<Spinner />
											กำลังยืนยัน...
										</>
									) : (
										'ยืนยันธุรกรรม'
									)}
								</Button>
							</div>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
